/**
 * Botón de la librería de UI (Radix `Slot` + Tailwind).
 *
 * Presentacional y accesible: no contiene lógica de negocio ni llama a la API
 * (`docs/frontend_architecture.md`, Pr. 6). Expone variantes de estilo del
 * proyecto (paleta azul `brand`) y tamaños. Con `asChild` delega el render en
 * el hijo (p. ej. un `<Link>` de React Router) conservando estilos y semántica,
 * en vez de anidar un `<a>` dentro de un `<button>`.
 */
import { Slot } from "@radix-ui/react-slot";
import { forwardRef, type ButtonHTMLAttributes } from "react";

import { cn } from "./cn";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Renderiza el hijo como raíz (Radix `Slot`) conservando estilos. */
  asChild?: boolean;
}

const BASE =
  "inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-60";

const VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-brand text-white hover:bg-brand-600",
  secondary:
    "border border-slate-300 bg-white text-slate-700 hover:bg-slate-100",
  ghost: "text-slate-700 hover:bg-slate-100",
  danger: "bg-red-600 text-white hover:bg-red-700",
};

const SIZES: Record<ButtonSize, string> = {
  sm: "h-8 px-3 text-sm",
  md: "h-10 px-4 text-sm",
  lg: "h-11 px-6 text-base",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    { variant = "primary", size = "md", asChild = false, className, type, ...props },
    ref,
  ) {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        // Un <button> sin `type` actúa como "submit" dentro de un form; fijamos
        // "button" por defecto. Con `asChild` el tipo no aplica (no es un botón).
        type={asChild ? undefined : (type ?? "button")}
        className={cn(BASE, VARIANTS[variant], SIZES[size], className)}
        {...props}
      />
    );
  },
);
